/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import Big from 'big.js';
import { Just, Left, Maybe, Right } from 'purify-ts';
import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import { modifiers as m } from 'vue-tsx-support';
import { parsers } from '@codegrade/utils';
import { valueProp } from './utils';
export default tsx.componentFactoryOf().create({
    name: 'decimal-editor',
    props: {
        name: p(String).default('Value'),
        value: valueProp().required,
        placeholder: p(String).optional,
        min: p.ofType().optional,
        max: p.ofType().optional,
        step: p.ofType().default(1),
        fixedDecimals: p.ofType().optional,
    },
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    data() {
        return {
            userInput: '',
        };
    },
    watch: {
        value: {
            immediate: true,
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            handler() {
                this.value.ifRight(maybeValue => maybeValue.caseOf({
                    Just: value => {
                        this.decodeValue(this.userInput)
                            .chain(existing => {
                            if (existing.eq(value)) {
                                return Right(value);
                            }
                            return Left('Change value');
                        })
                            .ifLeft(() => {
                            this.userInput = this.formatValue(value);
                        });
                    },
                    Nothing: () => {
                        this.userInput = '';
                    },
                }));
            },
        },
    },
    methods: {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        decodeValue(strValue) {
            return parsers.decimal.decode(strValue);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        onInput(strValue) {
            const toEmit = Maybe.fromPredicate(x => x !== '', strValue)
                .caseOf({
                Just: value => this.decodeValue(value).chain(this.checkValue),
                Nothing: () => Right(Maybe.empty()),
            })
                .mapLeft(err => new Error(err));
            tsx.emitOn(this, 'onInput', toEmit);
            this.userInput = strValue;
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        checkValue(value) {
            if (this.min != null && value.lt(this.min)) {
                return Left(`${this.name} should be greater than or equal to ${this.min.toString()}.`);
            }
            if (this.max != null && value.gt(this.max)) {
                return Left(`${this.name} should be less than or equal to ${this.max.toString()}.`);
            }
            return Right(Just(value));
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        reformatValue() {
            this.value.ifRight(maybeValue => {
                maybeValue.ifJust(value => {
                    this.userInput = this.formatValue(value);
                });
            });
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        formatValue(value) {
            if (this.fixedDecimals == null) {
                return value.toString();
            }
            else {
                return value.toFixed(this.fixedDecimals);
            }
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        incValue() {
            this.maybeUpdate(v => v.add(this.step));
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        decValue() {
            this.maybeUpdate(v => v.minus(this.step));
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        maybeUpdate(f) {
            this.decodeValue(this.userInput).ifRight(value => {
                let ret = f(value);
                if (this.max != null && ret.gt(this.max)) {
                    ret = new Big(this.max);
                }
                else if (this.min != null && ret.lt(this.min)) {
                    ret = new Big(this.min);
                }
                this.onInput(ret.toString());
            });
        },
    },
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    render() {
        const handleUp = m.up.prevent(this.incValue);
        const handleDown = m.down.prevent(this.decValue);
        return (<input name={this.name} placeholder={this.placeholder} class="form-control text-right" value={this.userInput} onInput={payload => this.onInput(payload.target.value)} onKeydown={event => {
                handleUp(event);
                handleDown(event);
                tsx.emitOn(this, 'onKeydown', event);
            }}/>);
    },
});
